import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Gavel, Clock, Users, TrendingUp, ChevronLeft, Timer, Shield, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuctionDetailPage = () => {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useApp();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [showBidConfirm, setShowBidConfirm] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, label: '' });

  const fetchAuction = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auctions/${auctionId}`);
      if (res.ok) {
        const data = await res.json();
        setAuction(data);
        // Set default bid amount
        const minBid = Math.max(data.current_bid || 0, data.minimum_price || 0) + 1;
        setBidAmount(minBid.toFixed(2));
      }
    } catch (err) {
      console.error('Error fetching auction:', err);
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  useEffect(() => {
    fetchAuction();
    const interval = setInterval(fetchAuction, 10000);
    return () => clearInterval(interval);
  }, [fetchAuction]);

  // Countdown timer
  useEffect(() => {
    if (!auction) return;
    const update = () => {
      const isUpcoming = auction.status === 'approved';
      const target = isUpcoming ? auction.start_time : auction.end_time;
      const diff = new Date(target) - new Date();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, label: isUpcoming ? 'Starting...' : 'Ended' });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        label: isUpcoming ? 'Starts in' : 'Ends in'
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [auction]);

  const handlePlaceBid = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to place a bid');
      navigate('/login');
      return;
    }
    setBidding(true);
    try {
      const res = await fetch(`${API_URL}/api/auctions/${auctionId}/bid?user_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parseFloat(bidAmount) })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Bid placed successfully!');
        setShowBidConfirm(false);
        fetchAuction();
      } else {
        toast.error(data.detail || 'Failed to place bid');
      }
    } catch (err) {
      toast.error('Error placing bid. Please try again.');
    } finally {
      setBidding(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Auction">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-amber-300 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!auction) {
    return (
      <Layout title="Auction">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Gavel className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Auction not found</h2>
          <Button onClick={() => navigate('/auctions')} variant="outline">Back to Auctions</Button>
        </div>
      </Layout>
    );
  }

  const isActive = auction.status === 'active';
  const isUpcoming = auction.status === 'approved';
  const isEnded = auction.status === 'sold' || auction.status === 'ended';
  const minBid = Math.max(auction.current_bid || 0, auction.minimum_price || 0);
  const isWinner = isEnded && auction.winner_id === user?.id;
  const isSeller = user?.id === auction.seller_id;
  const product = auction.product || {};

  return (
    <Layout>
      {/* Back button + status */}
      <div className="bg-white px-4 py-3 border-b flex items-center justify-between">
        <button onClick={() => navigate('/auctions')} className="flex items-center gap-1 text-gray-600 text-sm" data-testid="auction-back-btn">
          <ChevronLeft className="w-4 h-4" /> Auctions
        </button>
        {isActive && <Badge className="bg-red-500 text-white animate-pulse" data-testid="auction-live-badge">LIVE</Badge>}
        {isUpcoming && <Badge className="bg-blue-500 text-white">UPCOMING</Badge>}
        {isEnded && <Badge className="bg-gray-500 text-white">ENDED</Badge>}
      </div>

      {/* Product Image */}
      <div className="relative">
        {product.images?.length > 0 ? (
          <img src={product.images[0]} alt={auction.product_title} className="w-full h-64 object-cover" />
        ) : auction.product_image ? (
          <img src={auction.product_image} alt={auction.product_title} className="w-full h-64 object-cover" />
        ) : (
          <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
            <Gavel className="w-16 h-16 text-gray-400" />
          </div>
        )}
      </div>

      {/* Countdown Timer */}
      {!isEnded && (
        <div className={`px-4 py-4 ${isActive ? 'bg-red-50 border-b border-red-100' : 'bg-blue-50 border-b border-blue-100'}`}>
          <p className={`text-xs font-medium mb-2 ${isActive ? 'text-red-600' : 'text-blue-600'}`}>
            <Timer className="w-3.5 h-3.5 inline mr-1" />
            {timeLeft.label}
          </p>
          <div className="flex gap-3 justify-center" data-testid="auction-countdown">
            {[
              { val: timeLeft.days, label: 'Days' },
              { val: timeLeft.hours, label: 'Hours' },
              { val: timeLeft.minutes, label: 'Mins' },
              { val: timeLeft.seconds, label: 'Secs' }
            ].map(unit => (
              <div key={unit.label} className="text-center">
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center text-xl font-bold ${isActive ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'}`}>
                  {String(unit.val).padStart(2, '0')}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">{unit.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Winner banner */}
      {isWinner && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-600" />
          <p className="text-green-800 text-sm font-medium">You won this auction!</p>
        </div>
      )}

      {/* Auction Info */}
      <div className="px-4 py-4 bg-white">
        <h1 className="text-xl font-bold text-gray-900" data-testid="auction-title">{auction.product_title}</h1>
        <div className="flex items-center gap-4 mt-3">
          <div>
            <p className="text-xs text-gray-500">
              {isActive ? 'Current Bid' : isEnded ? 'Final Price' : 'Starting Price'}
            </p>
            <p className="text-3xl font-bold text-amber-600" data-testid="auction-current-price">
              ${(isEnded ? (auction.final_price || auction.current_bid) : (auction.current_bid > 0 ? auction.current_bid : auction.minimum_price))?.toFixed(2)}
            </p>
          </div>
          <div className="flex-1 flex gap-4 justify-end">
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">{auction.bid_count || 0}</p>
              <p className="text-[10px] text-gray-500">Bids</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">${auction.minimum_price?.toFixed(2)}</p>
              <p className="text-[10px] text-gray-500">Min Price</p>
            </div>
          </div>
        </div>

        {/* Seller info */}
        {auction.seller_name && (
          <div className="flex items-center gap-2 mt-4 pt-3 border-t">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm">
              {auction.seller_name.charAt(0)}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{auction.seller_name}</p>
              <p className="text-[10px] text-gray-500">Seller</p>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      {auction.description && (
        <div className="px-4 py-4 bg-white mt-2">
          <h3 className="font-semibold text-gray-900 text-sm mb-2">About this item</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{auction.description}</p>
        </div>
      )}

      {/* Product Details */}
      {product.condition && (
        <div className="px-4 py-4 bg-white mt-2">
          <h3 className="font-semibold text-gray-900 text-sm mb-2">Item Details</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">Condition</p>
              <p className="font-medium">{product.condition}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">Category</p>
              <p className="font-medium">{product.category}</p>
            </div>
            {product.location && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-gray-500">Location</p>
                <p className="font-medium">{product.location}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bid History */}
      {auction.bids?.length > 0 && (
        <div className="px-4 py-4 bg-white mt-2">
          <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-amber-500" />
            Bid History ({auction.bids.length})
          </h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {[...auction.bids].reverse().map((bid, i) => (
              <div key={bid.id || i} className={`flex items-center justify-between py-2 ${i < auction.bids.length - 1 ? 'border-b border-gray-50' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-[10px]">
                    {bid.user_name?.charAt(0) || '?'}
                  </div>
                  <span className="text-sm text-gray-700">{bid.user_name || 'Anonymous'}</span>
                  {i === 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Highest</Badge>}
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-900">${bid.amount?.toFixed(2)}</p>
                  <p className="text-[10px] text-gray-400">{new Date(bid.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bid Action Area */}
      {isActive && !isSeller && (
        <div className="fixed bottom-16 left-0 right-0 bg-white border-t shadow-lg px-4 py-3 z-40">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <Input
                type="number"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                className="pl-7 text-lg font-bold"
                min={minBid + 0.01}
                step="0.01"
                data-testid="bid-amount-input"
              />
            </div>
            <Button
              onClick={() => {
                if (!isAuthenticated) {
                  toast.error('Please log in to bid');
                  navigate('/login');
                  return;
                }
                if (parseFloat(bidAmount) <= minBid) {
                  toast.error(`Bid must be more than $${minBid.toFixed(2)}`);
                  return;
                }
                setShowBidConfirm(true);
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-6"
              data-testid="place-bid-btn"
            >
              <Gavel className="w-4 h-4 mr-2" />
              Bid
            </Button>
          </div>
        </div>
      )}

      {/* Spacer for fixed bid bar */}
      {isActive && !isSeller && <div className="h-20" />}

      {/* Bid Confirmation Dialog */}
      <Dialog open={showBidConfirm} onOpenChange={setShowBidConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Your Bid</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="text-center mb-4">
              <p className="text-3xl font-bold text-amber-600">${parseFloat(bidAmount || 0).toFixed(2)}</p>
              <p className="text-sm text-gray-500 mt-1">for "{auction.product_title}"</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                By placing this bid, you agree to purchase this item at your bid price if you win the auction.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBidConfirm(false)}>Cancel</Button>
            <Button
              onClick={handlePlaceBid}
              disabled={bidding}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-bid-btn"
            >
              {bidding ? 'Placing bid...' : 'Confirm Bid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AuctionDetailPage;
